import { useCallback, useEffect, useMemo, useState } from 'react'
import { s } from './lib/sx'
import { useLocation } from './lib/router'
import { search } from './lib/search'
import { useCounters } from './lib/useCounters'
import { config } from './config'
import { Header } from './components/Header'
import { Footer } from './components/Footer'
import { Home } from './pages/Home'
import { About } from './pages/About'
import { Leadership } from './pages/Leadership'
import { Safety } from './pages/Safety'
import { Services } from './pages/Services'
import { ServiceDetail } from './pages/ServiceDetail'
import { Sectors } from './pages/Sectors'
import { Projects } from './pages/Projects'
import { ProjectDetail } from './pages/ProjectDetail'
import { Careers } from './pages/Careers'
import { News } from './pages/News'
import { Contact } from './pages/Contact'
import { Quote, type QuoteDraft } from './pages/Quote'
import { Paia, Privacy, SupplierCode } from './pages/Legal'
import { NotFound } from './pages/NotFound'
import { counterDefs } from './content/company'
import { services, serviceDetail } from './content/services'
import { projects } from './content/projects'

/** Routes whose second path segment is a content slug. */
const DETAIL_ROUTES = new Set(['services', 'projects'])

const TITLES: Record<string, string> = {
  home: 'Samloryx Engineering',
  about: 'About — Samloryx Engineering',
  leadership: 'Leadership — Samloryx Engineering',
  safety: 'Safety & compliance — Samloryx Engineering',
  services: 'Services — Samloryx Engineering',
  sectors: 'Sectors — Samloryx Engineering',
  projects: 'Projects — Samloryx Engineering',
  careers: 'Careers — Samloryx Engineering',
  news: 'News & insights — Samloryx Engineering',
  contact: 'Contact — Samloryx Engineering',
  quote: 'Request a quote — Samloryx Engineering',
  paia: 'PAIA manual — Samloryx Engineering',
  privacy: 'Privacy notice — Samloryx Engineering',
  'supplier-code': 'Supplier code of conduct — Samloryx Engineering',
}

function useViewportWidth(): number {
  const [w, setW] = useState(() => (typeof window === 'undefined' ? 1440 : window.innerWidth))
  useEffect(() => {
    const onResize = () => setW(window.innerWidth)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  return w
}

export function App() {
  const { route, param } = useLocation()
  const w = useViewportWidth()
  const narrow = w < config.narrowBreakpoint

  // Persists across navigation, matching the prototype.
  const [filter, setFilter] = useState(config.defaultSector)
  const [openJob, setOpenJob] = useState<string | null>(null)
  const [draft, setDraftState] = useState<QuoteDraft>({})

  // Reset on every navigation, matching the prototype's hashchange handler.
  const [menu, setMenu] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [q, setQ] = useState('')
  const [step, setStep] = useState(1)
  const [submitted, setSubmitted] = useState(false)
  const [applied, setApplied] = useState(false)

  useEffect(() => {
    setMenu(false)
    setSearchOpen(false)
    setQ('')
    setStep(1)
    setSubmitted(false)
    setApplied(false)
    window.scrollTo(0, 0)
  }, [route, param])

  // Close the menu once the viewport is wide enough to show the nav inline.
  useEffect(() => {
    if (w >= config.navBreakpoint) setMenu(false)
  }, [w])

  useEffect(() => {
    document.title = TITLES[route] ?? 'Samloryx Engineering'
  }, [route])

  const counters = useCounters(counterDefs, config.counterDuration)
  const results = useMemo(() => search(q), [q])

  const setDraft = useCallback((patch: Partial<QuoteDraft>) => {
    setDraftState((d) => ({ ...d, ...patch }))
  }, [])

  const service = route === 'services' && param ? services.find((sv) => sv.slug === param) : undefined
  const project = route === 'projects' && param ? projects.find((p) => p.slug === param) : undefined

  /** An unknown slug under a detail route is a 404, not a silent fall-through to the index. */
  const badSlug = Boolean(param) && DETAIL_ROUTES.has(route) && !service && !project
  const deepParam = Boolean(param) && !DETAIL_ROUTES.has(route)

  let page
  if (badSlug || deepParam) {
    page = <NotFound />
  } else if (service) {
    page = <ServiceDetail service={service} detail={serviceDetail[service.slug]} />
  } else if (project) {
    page = <ProjectDetail project={project} />
  } else {
    switch (route) {
      case 'home':
        page = <Home narrow={narrow} counters={counters} />
        break
      case 'about':
        page = <About />
        break
      case 'leadership':
        page = <Leadership />
        break
      case 'safety':
        page = <Safety />
        break
      case 'services':
        page = <Services />
        break
      case 'sectors':
        page = <Sectors />
        break
      case 'projects':
        page = <Projects filter={filter} setFilter={setFilter} />
        break
      case 'careers':
        page = <Careers openJob={openJob} setOpenJob={setOpenJob} applied={applied} setApplied={setApplied} />
        break
      case 'news':
        page = <News />
        break
      case 'contact':
        page = <Contact />
        break
      case 'quote':
        page = <Quote step={step} setStep={setStep} draft={draft} setDraft={setDraft} submitted={submitted} setSubmitted={setSubmitted} />
        break
      case 'paia':
        page = <Paia />
        break
      case 'privacy':
        page = <Privacy />
        break
      case 'supplier-code':
        page = <SupplierCode />
        break
      default:
        page = <NotFound />
    }
  }

  return (
    <div style={s('min-height:100vh;background:#F2F4F6;')}>
      <Header
        w={w}
        menu={menu}
        toggleMenu={() => setMenu((m) => !m)}
        closeMenu={() => setMenu(false)}
        searchOpen={searchOpen}
        toggleSearch={() => {
          setSearchOpen((o) => !o)
          setQ('')
        }}
        closeSearch={() => {
          setSearchOpen(false)
          setQ('')
        }}
        q={q}
        onSearch={setQ}
        results={results}
        noResults={q.trim().length > 0 && results.length === 0}
      />
      <main style={s('min-height:60vh;')}>{page}</main>
      <Footer />
    </div>
  )
}
